/**
 * Generic credential UI for one authenticated MCP, driven entirely by the
 * registry row's ``credentialFields`` (a SAFE projection of its JSON-Schema —
 * field metadata only, never values) plus an optional ``credentialUi`` hint.
 *
 * Two layouts, chosen by ``mcp.credentialUi.layout``:
 *   - ``"form"``   — a flat field list (default; fine for 1-few fields).
 *   - ``"wizard"`` — a Connectors-style horizontal stepper. Fields are grouped
 *     by their ``group`` into the ordered ``credentialUi.steps``, followed by a
 *     Review step. Lets a complex MCP present sectioned config without a long
 *     scroll.
 *
 * Field types: ``string`` (text or masked password via ``secret``), ``boolean``
 * (toggle), and a ``<select>`` when ``enum`` is present. Schema ``default``
 * seeds the initial value.
 *
 * The platform stores entered secrets in SSM via the keys-put broker and keeps
 * only a pointer in DynamoDB; this form NEVER receives an existing secret back —
 * it shows configured / not-configured status and lets the user (re)enter or
 * clear values. RBAC is enforced server-side; ``canManage`` only gates the UI.
 *
 * NOTE: there is no MCP connection-test probe yet (the lifecycle phase will add
 * it). When ``credentialUi.testSupported`` is true a Test step can be added;
 * until then we never show a fake "tested OK".
 */
import React, { useEffect, useMemo, useState } from 'react';

/** Seed initial draft values from schema defaults (booleans → bool, else ''). */
function initialDraft(fields, overrides = {}) {
  const d = {};
  for (const f of fields) {
    const has = overrides && Object.prototype.hasOwnProperty.call(overrides, f.key);
    if (f.type === 'boolean') d[f.key] = has ? Boolean(overrides[f.key]) : Boolean(f.default);
    else if (has && overrides[f.key] != null) d[f.key] = String(overrides[f.key]);
    else if (f.default != null) d[f.key] = String(f.default);
    else d[f.key] = '';
  }
  return d;
}

/** Build the credentials payload the API expects from the current draft.
 *  Booleans always sent; strings trimmed, blanks dropped (server treats a
 *  blank optional as "unset"). */
function collectCredentials(fields, draft) {
  const creds = {};
  for (const f of fields) {
    if (f.type === 'boolean') {
      creds[f.key] = Boolean(draft[f.key]);
    } else {
      const v = String(draft[f.key] ?? '').trim();
      if (v) creds[f.key] = v;
    }
  }
  return creds;
}

/** One field's input — text / password / toggle / select. ``id`` links it to
 *  the FieldRow label for a11y (and lets getByLabelText find it). */
function FieldInput({ field: f, value, configured, onChange, id }) {
  if (f.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          id={id}
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        {f.label || f.key}
      </label>
    );
  }
  if (Array.isArray(f.enum) && f.enum.length) {
    return (
      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
      >
        {f.enum.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      id={id}
      type={f.secret === false ? 'text' : 'password'}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      autoComplete="off"
      placeholder={
        f.secret !== false && configured
          ? '•••••••• (set — enter to replace)'
          : (f.placeholder || '')
      }
      className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
    />
  );
}

/** A labelled field row (skips its own label for booleans — the toggle is
 *  self-labelling). */
function FieldRow({ field: f, value, configured, onChange }) {
  const inputId = `mcp-field-${f.key}`;
  return (
    <div>
      {f.type !== 'boolean' ? (
        <label htmlFor={inputId} className="block text-xs font-medium text-gray-700 mb-1">
          {f.label || f.key}
          {f.required ? <span className="text-red-500"> *</span> : null}
        </label>
      ) : null}
      <FieldInput id={inputId} field={f} value={value} configured={configured} onChange={onChange} />
      {f.hint ? <p className="mt-0.5 text-[11px] text-gray-500">{f.hint}</p> : null}
    </div>
  );
}

export default function McpCredentialForm({
  /** Registry row: { id, displayName, description, credentialFields[], credentialUi }. */
  mcp,
  /** Room/agent status row for this MCP: { configured, scope } (may be undefined). */
  status,
  /** true if the viewer may set/clear (room OWNER/BUILDER or ws OWNER/ADMIN). */
  canManage = false,
  /** Pre-fill the instance label when editing an existing instance. */
  initialLabel = '',
  /** Edit mode: saved NON-SECRET field values to pre-populate (e.g. host, branch,
   *  repo url). Secret fields are not included — see ``initialSecretsSet``. */
  initialValues = null,
  /** Edit mode: secret field keys that already have a saved value (shown as
   *  "set — enter to replace", left blank = keep existing). */
  initialSecretsSet = null,
  /** async (mcpId, credentials, { label }) => void */
  onSave,
  /** async (mcpId) => void */
  onClear,
  /** async (mcpId, credentials) => { ok, message, details } — verify without
   *  saving. Only called when ``mcp.credentialUi.testSupported`` is true. */
  onTest,
}) {
  const fields = useMemo(
    () => (Array.isArray(mcp?.credentialFields) ? mcp.credentialFields : []),
    [mcp],
  );
  const ui = mcp?.credentialUi || {};
  const isWizard = ui.layout === 'wizard' && fields.length > 0;

  // Ordered wizard steps from the schema, keeping only steps that actually
  // have fields; fields with no/unknown group fall into a trailing "Other".
  // Steps whose fields are ALL optional are pushed AFTER the steps that carry a
  // required field, so the flow is: required config → optional config → Name.
  // (The synthetic "Name" step — the multi-instance label — is always last and
  // is appended in `allSteps` below.)
  const steps = useMemo(() => {
    if (!isWizard) return [];
    const declared = Array.isArray(ui.steps) ? ui.steps : [];
    const byGroup = new Map();
    for (const f of fields) {
      const g = (f.group && declared.includes(f.group)) ? f.group : '__other__';
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g).push(f);
    }
    const built = [];
    for (const g of declared) {
      if (byGroup.has(g)) built.push({ title: g, fields: byGroup.get(g) });
    }
    if (byGroup.has('__other__')) built.push({ title: 'Other', fields: byGroup.get('__other__') });
    // Stable partition: required-bearing groups first, optional-only groups last.
    const hasRequired = (s) => s.fields.some((f) => f.required && f.type !== 'boolean');
    const required = built.filter(hasRequired);
    const optional = built.filter((s) => !hasRequired(s));
    return [...required, ...optional];
  }, [isWizard, ui.steps, fields]);

  // Multi-instance label: how this connection appears in the catalog/picker and
  // what makes it a distinct instance (the backend mints a new instanceId from a
  // non-empty label). Always the final wizard step.
  const [label, setLabel] = useState(() => String(initialLabel || '').trim());
  const [draft, setDraft] = useState(() => initialDraft(fields, initialValues || {}));
  const [stepIdx, setStepIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { kind: 'ok'|'error', text }
  // ``configured`` drives the secret-field placeholder ("•••• set — enter to
  // replace"). True when editing an existing row OR the parent flags it.
  const editingSecretKeys = useMemo(
    () => new Set(Array.isArray(initialSecretsSet) ? initialSecretsSet : []),
    [initialSecretsSet],
  );
  const configured = Boolean(status?.configured) || editingSecretKeys.size > 0;
  const initialValuesKey = JSON.stringify(initialValues || null);

  // Re-seed when the selected MCP changes, or when edit-mode prefill arrives
  // (parent also keys us by agent:mcp). ``initialValues`` pre-populates the
  // non-secret fields; secret fields stay blank (masked, optional to replace).
  useEffect(() => {
    setDraft(initialDraft(fields, initialValues || {}));
    setLabel(String(initialLabel || '').trim());
    setStepIdx(0);
    setMsg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, initialLabel, initialValuesKey]);

  const setField = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setMsg(null);
  };

  // A required field is satisfied when it has a value, OR (edit mode) it's an
  // already-set secret left blank to keep — so editing doesn't force re-typing
  // the PAT just to advance/save (parity with the connector wizard).
  const requiredSatisfied = (f) =>
    String(draft[f.key] ?? '').trim().length > 0 || editingSecretKeys.has(f.key);
  const missingRequired = fields
    .filter((f) => f.required && f.type !== 'boolean')
    .some((f) => !requiredSatisfied(f));
  // Something to save: any non-default string typed, or (when not yet
  // configured) we still allow saving defaults + required fields.
  const hasAnyInput = fields.some((f) => {
    if (f.type === 'boolean') return false;
    return String(draft[f.key] ?? '').trim().length > 0;
  });

  const totalSteps = steps.length; // field steps; the "Name" step is at index totalSteps
  // Final step is "Name" — it carries the multi-instance label, a review of the
  // entered values, and the Test/Save actions.
  const onNameStep = isWizard && stepIdx >= totalSteps;

  const labelTrimmed = String(label || '').trim();
  // The label is what makes a saved credential a named, multi-instance
  // connection (the backend mints a fresh instanceId from a non-empty label).
  // Require it whenever there's anything to configure — wizard or flat form —
  // so "Create New" never silently collapses onto the single "default" instance.
  const labelMissing = fields.length > 0 && !labelTrimmed;

  const handleSave = async () => {
    if (!canManage || busy || missingRequired || labelMissing) return;
    const credentials = collectCredentials(fields, draft);
    setBusy(true);
    setMsg(null);
    try {
      await onSave(mcp.id, credentials, { label: labelTrimmed });
      setDraft(initialDraft(fields));
      setLabel('');
      setStepIdx(0);
      setMsg({ kind: 'ok', text: 'Saved. This connection is ready for the selected scope.' });
    } catch (e) {
      setMsg({ kind: 'error', text: e?.message || 'Could not save credentials.' });
    } finally {
      setBusy(false);
    }
  };

  const [testing, setTesting] = useState(false);
  const canTest = Boolean(ui.testSupported && onTest);
  const handleTest = async () => {
    if (!canTest || testing || busy || missingRequired) return;
    setTesting(true);
    setMsg(null);
    try {
      const res = await onTest(mcp.id, collectCredentials(fields, draft));
      if (res?.ok) {
        const extra = res.details?.login ? ` (${res.details.login})` : '';
        // Be explicit: this verifies the credential, not the live MCP server —
        // the first room run exercises the tool end-to-end.
        setMsg({
          kind: 'ok',
          text: `${res.message || 'Credential verified.'}${extra} The first run in a room exercises the live tool.`,
        });
      } else {
        setMsg({ kind: 'error', text: res?.message || 'Verification failed.' });
      }
    } catch (e) {
      setMsg({ kind: 'error', text: e?.message || 'Verification failed.' });
    } finally {
      setTesting(false);
    }
  };

  const handleClear = async () => {
    if (!canManage || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await onClear(mcp.id);
      setDraft(initialDraft(fields));
      setStepIdx(0);
      setMsg({ kind: 'ok', text: 'Cleared.' });
    } catch (e) {
      setMsg({ kind: 'error', text: e?.message || 'Could not clear credentials.' });
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">{mcp?.displayName || mcp?.id}</p>
        {mcp?.description ? (
          <p className="mt-0.5 text-[11px] text-gray-500">{mcp.description}</p>
        ) : null}
      </div>
      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
          configured ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
        }`}
        title={configured && status?.scope
          ? `Configured at ${String(status.scope).toLowerCase()} scope` : undefined}
      >
        {configured ? 'Connected' : 'Not connected'}
      </span>
    </div>
  );

  const footerMsg = msg ? (
    <p className={`mt-2 text-[11px] ${msg.kind === 'error' ? 'text-red-600' : 'text-emerald-700'}`}>
      {msg.text}
    </p>
  ) : null;

  if (fields.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 p-3">
        {header}
        <p className="mt-2 text-[11px] text-gray-500">This connection has no configurable fields.</p>
      </div>
    );
  }

  // ── Flat form ──────────────────────────────────────────────────────────
  if (!isWizard) {
    return (
      <div className="rounded-lg border border-gray-200 p-3">
        {header}
        <fieldset disabled={!canManage || busy} className="mt-3 space-y-2">
          <legend className="sr-only">Credentials for {mcp?.displayName || mcp?.id}</legend>
          {fields.map((f) => (
            <FieldRow key={f.key} field={f} value={draft[f.key]} configured={configured}
                      onChange={(v) => setField(f.key, v)} />
          ))}
          {/* Connection name — last field; the multi-instance label. */}
          <div className="pt-1">
            <label htmlFor="mcp-instance-label-flat" className="block text-xs font-medium text-gray-700 mb-1">
              Connection name <span className="text-red-500">*</span>
            </label>
            <input
              id="mcp-instance-label-flat"
              type="text"
              value={label}
              maxLength={120}
              placeholder={`e.g. ${mcp?.displayName || mcp?.id} · work`}
              onChange={(e) => { setLabel(e.target.value); setMsg(null); }}
              className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
            />
            <p className="mt-1 text-[11px] leading-snug text-gray-500">
              How this connection appears in the catalog and pickers — and what
              makes it a distinct instance.
            </p>
          </div>
        </fieldset>
        {canManage ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" onClick={handleSave}
                    disabled={busy || missingRequired || labelMissing || (!hasAnyInput && !configured)}
                    className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    title={missingRequired ? 'Fill the required fields first' : labelMissing ? 'Give this connection a name first' : undefined}>
              {busy ? 'Saving…' : configured ? 'Replace' : 'Save'}
            </button>
            {canTest ? (
              <button type="button" onClick={handleTest} disabled={busy || testing || missingRequired}
                      className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      title="Verify the credential against the service (does not start the MCP server)">
                {testing ? 'Testing…' : 'Test'}
              </button>
            ) : null}
            {configured ? (
              <button type="button" onClick={handleClear} disabled={busy}
                      className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                Clear
              </button>
            ) : null}
          </div>
        ) : (
          <p className="mt-3 text-[11px] text-gray-500">Read-only.</p>
        )}
        {footerMsg}
      </div>
    );
  }

  // ── Wizard ───────────────────────────────────────────────────────────────
  // Final step is "Name" (the multi-instance label + review + save), mirroring
  // the connector wizard. Optional-only field steps already sort just before it.
  const stepTitles = [...steps.map((s) => s.title), 'Name'];
  const current = onNameStep ? null : steps[stepIdx];
  // A field step is blocked from advancing if one of ITS required fields is empty.
  const stepBlocked = current
    ? current.fields.some((f) => f.required && f.type !== 'boolean' && !requiredSatisfied(f))
    : false;

  return (
    <div className="rounded-lg border border-gray-200 p-3">
      {header}

      {/* Stepper */}
      <ol className="mt-3 flex items-center gap-1 text-[11px]">
        {stepTitles.map((title, i) => {
          const state = i === stepIdx ? 'active' : i < stepIdx ? 'done' : 'todo';
          return (
            <li key={title} className="flex items-center gap-1">
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold ${
                state === 'active' ? 'bg-blue-600 text-white'
                  : state === 'done' ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-100 text-gray-500'}`}>
                {i + 1}
              </span>
              <span className={state === 'active' ? 'font-medium text-gray-900' : 'text-gray-500'}>
                {title}
              </span>
              {i < stepTitles.length - 1 ? <span className="mx-1 text-gray-300">→</span> : null}
            </li>
          );
        })}
      </ol>

      <fieldset disabled={!canManage || busy} className="mt-3 space-y-2">
        <legend className="sr-only">Configure {mcp?.displayName || mcp?.id}</legend>
        {current ? (
          current.fields.map((f) => (
            <FieldRow key={f.key} field={f} value={draft[f.key]} configured={configured}
                      onChange={(v) => setField(f.key, v)} />
          ))
        ) : (
          // Name step — the multi-instance label, then a review of entered values.
          <div className="space-y-3">
            <div>
              <label htmlFor="mcp-instance-label" className="block text-xs font-medium text-gray-700 mb-1">
                Connection name <span className="text-red-500">*</span>
              </label>
              <input
                id="mcp-instance-label"
                type="text"
                value={label}
                maxLength={120}
                placeholder={`e.g. ${mcp?.displayName || mcp?.id} · work`}
                onChange={(e) => { setLabel(e.target.value); setMsg(null); }}
                className="block w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
              />
              <p className="mt-1 text-[11px] leading-snug text-gray-500">
                How this connection appears in the catalog and pickers. The name is
                what makes it a distinct instance — use it to keep several (e.g.
                <span className="font-medium"> work</span> vs
                <span className="font-medium"> personal</span>) side by side.
              </p>
            </div>

            <div className="space-y-1 border-t border-gray-100 pt-2 text-[11px]">
              {fields.map((f) => {
                let shown;
                if (f.type === 'boolean') shown = draft[f.key] ? 'on' : 'off';
                else if (f.secret !== false) shown = String(draft[f.key] ?? '').trim() ? '•••••••• (set)' : '(not set)';
                else shown = String(draft[f.key] ?? '').trim() || '(default)';
                return (
                  <div key={f.key} className="flex justify-between gap-2">
                    <span className="text-gray-500">{f.label || f.key}</span>
                    <span className="font-medium text-gray-800">{shown}</span>
                  </div>
                );
              })}
              {canTest ? (
                <p className="pt-1 text-gray-500">
                  Use <span className="font-medium">Test</span> to verify the credential
                  against the service. (This checks the credential, not that the MCP
                  server starts — that happens on the first run.)
                </p>
              ) : (
                <p className="pt-1 text-amber-700">
                  Connection testing isn’t available for this MCP yet — credentials are
                  saved as entered.
                </p>
              )}
            </div>
          </div>
        )}
      </fieldset>

      {canManage ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {stepIdx > 0 ? (
            <button type="button" onClick={() => setStepIdx((i) => i - 1)} disabled={busy}
                    className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Back
            </button>
          ) : null}
          {!onNameStep ? (
            <button type="button" onClick={() => setStepIdx((i) => i + 1)} disabled={busy || stepBlocked}
                    className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    title={stepBlocked ? 'Fill the required fields first' : undefined}>
              Next
            </button>
          ) : (
            <>
              {canTest ? (
                <button type="button" onClick={handleTest} disabled={busy || testing || missingRequired}
                        className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        title="Verify the credential against the service (does not start the MCP server)">
                  {testing ? 'Testing…' : 'Test'}
                </button>
              ) : null}
              <button type="button" onClick={handleSave} disabled={busy || missingRequired || labelMissing}
                      className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      title={labelMissing ? 'Give this connection a name first' : undefined}>
                {busy ? 'Saving…' : configured ? 'Replace' : 'Save'}
              </button>
            </>
          )}
          {configured ? (
            <button type="button" onClick={handleClear} disabled={busy}
                    className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Clear
            </button>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-[11px] text-gray-500">Read-only.</p>
      )}
      {footerMsg}
    </div>
  );
}
